import { ArrowCounterClockwise } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import RenderIf from "common/components/RenderIf";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import Checkbox from "common/components/ui/Checkbox";
import { useCampaignContext } from "common/helpers/CampaignContext";
import useDebounce from "common/hooks/useDebounce";
import useIntersection from "common/hooks/useIntersection";
import type { LinkedInProfile } from "common/types";

import { DEFAULT_AUDIENCE_FILTERS } from "../../../constants";
import useAudienceSearch from "../../../datahooks/useAudienceSearch";
import useTargeting from "../../../datahooks/useTargeting";
import useTargetingMutations from "../../../datahooks/useTargetingMutations";
import useAudienceFilters from "../../../hooks/useAudienceFilters";
import ProfileCard from "../../ProfileCard";
import StepperNextButton from "../../StepperNextButton";
import Stepper from "../Stepper";
import AudienceDetails from "./AudienceDetails";
import AudienceFilters from "./AudienceFilters";
import AudienceHeader from "./AudienceHeader";
import AudienceProgress from "./AudienceProgress";
import ExportAudience from "./ExportAudience";
import RemoveTargets from "./RemoveTargets";
import useAudienceProgress from "./useAudienceProgress";

import type { AudienceStats } from "../../../types";

import NoTargetsFoundImage from "assets/images/empty-placeholders/no-profiles.png";
import ProgressImage from "assets/images/empty-placeholders/progress.png";
import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";

const PAGE_SIZE = 24;

export default function Audience({
  audienceStats,
  openLeadPreview,
}: {
  audienceStats: AudienceStats;
  openLeadPreview: (leadId: string) => void;
}) {
  const navigate = useNavigate();
  const { ref, isIntersecting } = useIntersection();
  const { type: campaignType } = useCampaignContext();
  const { t } = useTranslation("campaigns", { keyPrefix: "audience" });

  const { removeTargeting, isRemovingTargeting } = useTargetingMutations();
  const { targeting } = useTargeting();

  const [filters, setFilters] = useState(DEFAULT_AUDIENCE_FILTERS);
  const [selectedProfiles, setSelectedProfiles] = useState<LinkedInProfile[]>(
    [],
  );

  function toggleSelection(profile: LinkedInProfile) {
    setSelectedProfiles((prevSelectedProfiles) => {
      if (prevSelectedProfiles.find(({ id }) => id === profile.id)) {
        return prevSelectedProfiles.filter(({ id }) => id !== profile.id);
      }

      return [...prevSelectedProfiles, profile];
    });
  }

  function resetSelectedProfiles() {
    setSelectedProfiles([]);
  }

  const { resetAllFilters } = useAudienceFilters(filters, setFilters);

  const { keyword, ...audienceFilters } = filters;
  const searchValue = keyword;
  const [debouncedSearchValue] = useDebounce(searchValue, 300);

  const hasActiveSearchParams =
    Object.values(audienceFilters).some((filter) =>
      Array.isArray(filter) ? !!filter.length : !!filter,
    ) || debouncedSearchValue;

  const {
    audience,
    isLoadingAudience,
    isFetchingMoreProfiles,
    audienceFacets,
    fetchNextPage,
    hasNextPage,
  } = useAudienceSearch({
    ...filters,
    keyword: debouncedSearchValue,
  });

  const isMiningTargets = audienceStats && audienceStats.progress !== 100;
  const hasSelectedProfiles = selectedProfiles.length > 0;

  useAudienceProgress(filters, isMiningTargets);

  useEffect(() => {
    if (isIntersecting && hasNextPage && !isFetchingMoreProfiles) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingMoreProfiles, isIntersecting, fetchNextPage]);

  let mainContent = (
    // Default fallback - no targets found
    <EmptyPlaceholder
      title={t("noTargetsFoundTitle")}
      subtitle={t("noTargetsFoundSubtitle")}
      imageSrc={NoTargetsFoundImage}
      actionComponent={
        <div className="flex flex-col gap-2 lg:flex-row">
          <Button
            onClick={() => removeTargeting()}
            isLoading={isRemovingTargeting}
          >
            Edit targeting
          </Button>
          <AudienceDetails audienceStats={audienceStats} />
        </div>
      }
    />
  );

  const isList = campaignType === "list" || campaignType === "crm";

  const isEmptyCampaign =
    isList &&
    targeting &&
    "profiles" in targeting &&
    !targeting.profiles.length &&
    !audience.length;

  if (isEmptyCampaign) {
    mainContent = (
      <EmptyPlaceholder
        title="No targets found"
        subtitle="This campaign does not have any targets. You can add targets through integrations, via API, or manually."
        imageSrc={NoTargetsFoundImage}
      />
    );
    // Show mining progress when targets are being mined and no audience data yet
  } else if (isMiningTargets && (!audience || audience.length === 0)) {
    mainContent = (
      <EmptyPlaceholder
        title={t("loadingTargetsTitle")}
        subtitle={t("loadingTargetsSubtitle")}
        imageSrc={ProgressImage}
      />
    );
  } else if (isLoadingAudience || (audience && audience.length > 0)) {
    mainContent = (
      <>
        <section className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {(isLoadingAudience ? new Array(PAGE_SIZE).fill({}) : audience).map(
            (profile, index) => (
              <ProfileCard
                key={profile ? profile.id : index}
                profile={profile}
                isLoading={isLoadingAudience}
                isSelected={
                  !!selectedProfiles.find(({ id }) => profile?.id === id)
                }
                onSelect={() => toggleSelection(profile)}
                openPreview={openLeadPreview}
              />
            ),
          )}

          <RenderIf condition={isFetchingMoreProfiles}>
            {new Array(PAGE_SIZE).fill({}).map((_, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: no unique key
              <ProfileCard key={`new-profile-${index}`} isLoading />
            ))}
          </RenderIf>
        </section>

        <div ref={ref} className="opacity-0">
          Load more content
        </div>
      </>
    );
  }
  // Priority 3: Show no results when search/filters return empty
  else if (hasActiveSearchParams && (!audience || audience.length === 0)) {
    mainContent = (
      <EmptyPlaceholder
        title="No results found"
        subtitle="There were no results found for your search. Try adjusting your filters or search terms."
        imageSrc={NoResultsImage}
        actionComponent={
          <Button
            leftIcon={<ArrowCounterClockwise />}
            variant="secondary-black"
            onClick={resetAllFilters}
          >
            Clear filters
          </Button>
        }
      />
    );
  }

  return (
    <>
      <AudienceHeader
        isMiningTargets={isMiningTargets}
        filters={filters}
        setFilters={setFilters}
      />

      {!isEmptyCampaign && (
        <AudienceProgress
          progress={audienceStats ? audienceStats.progress : 0}
        />
      )}

      <div className="flex flex-col items-center justify-between gap-2 lg:flex-row pb-3 lg:border-b lg:border-dashed lg:border-b-black-200">
        <SearchInput
          className="lg:w-[320px]"
          placeholder="Search audience"
          value={searchValue}
          onChange={(e) =>
            setFilters({ ...filters, keyword: e.currentTarget.value })
          }
          onClear={() => setFilters({ ...filters, keyword: "" })}
          disabled={isMiningTargets}
        />

        {/* Action buttons */}
        <div className="flex gap-2.5 max-lg:w-full">
          {hasSelectedProfiles && (
            <Button
              intent="iconOnly"
              variant="outline-black"
              onClick={resetSelectedProfiles}
            >
              <div>
                <Checkbox isStyleOnly checked="indeterminate" />
              </div>
            </Button>
          )}

          {hasActiveSearchParams || hasSelectedProfiles ? (
            <RemoveTargets
              filters={filters}
              hasSelectedProfiles={hasSelectedProfiles}
              isMiningTargets={isMiningTargets}
              selectedProfiles={selectedProfiles}
              profilesCount={audience ? audience.length : 0}
              resetAllFilters={resetAllFilters}
              resetSelectedProfiles={resetSelectedProfiles}
            />
          ) : (
            <AudienceDetails audienceStats={audienceStats} />
          )}

          <ExportAudience
            filters={filters}
            audience={audience}
            selectedProfiles={selectedProfiles}
            isDisabled={audience.length === 0}
          />
        </div>
      </div>

      <AudienceFilters
        audienceFacets={audienceFacets}
        isLoadingFacets={isLoadingAudience}
        isMiningTargets={isMiningTargets}
        setFilters={(newFilters) => {
          // reset selected profiles when filters change
          resetSelectedProfiles();
          setFilters(newFilters);
        }}
        filters={filters}
      />

      {mainContent}

      <Stepper
        selectedIndex={1}
        isMiningTargets={isMiningTargets}
        nextButtonComponent={
          <StepperNextButton
            disabled={!isList && (!audienceStats || !audienceStats.valid)}
            onClick={() => navigate("../flows")}
          />
        }
      />
    </>
  );
}
